import { useState, useMemo } from 'react';

//Component
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { useAuthContext } from '../../context/auth-context';

//Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';

import { UserActions } from './user-actions';
import { roleLabel } from '../../constants/roles';

export const ExpandableRow = props => {
  // Desasignar una rúbrica es una escritura: exige `rubrics.manage`, igual que el
  // resto de la gestión de rúbricas. El icono se pintaba siempre y para un `sec` (o
  // bajo "Ver como") solo llevaba a un 403.
  const { hasPermission } = useAuthContext();
  const puedeGestionarRubricas = hasPermission('rubrics.manage');
  const {
    row,
    activeCampId,
    handleShowConfirmationDialog,
    handleResetPassword,
    handleUnassignCamp,
    handleChangeRole,
    handleEditPermissions,
    handleManageCamps,
  } = props;
  const [open, setOpen] = useState(false);
  const [eventFilter, setEventFilter] = useState('');

  const uniqueEvents = useMemo(() => {
    const seen = new Map();
    for (const rubric of row.rubric || []) {
      const id = rubric.event?._id;
      if (id && !seen.has(id)) {
        seen.set(id, rubric.event.name || '—');
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [row.rubric]);

  const visibleRubrics = useMemo(() => {
    if (!eventFilter) return row.rubric || [];
    return (row.rubric || []).filter(r => r.event?._id === eventFilter);
  }, [row.rubric, eventFilter]);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          {
            <IconButton
              aria-label='expand row'
              disabled={row.rubric?.length === 0}
              size='small'
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          }
        </TableCell>
        <TableCell component='th' scope='row'>
          {row.fullname}
        </TableCell>
        <TableCell align='right'>{row.username}</TableCell>
        <TableCell align='right'>{row.email || '—'}</TableCell>
        <TableCell align='right'>
          {roleLabel(row.role)}
          {row.zone && (
            <Typography variant='caption' color='text.secondary' display='block'>
              {row.zone}
            </Typography>
          )}
        </TableCell>
        <TableCell align='right'>
          <UserActions
            user={row}
            activeCampId={activeCampId}
            onChangeRole={handleChangeRole}
            onEditPermissions={handleEditPermissions}
            onManageCamps={handleManageCamps}
            onResetPassword={handleResetPassword}
            onUnassignCamp={handleUnassignCamp}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant='h6' component='div'>
                  Rubricas Asignadas a usuario {row.username}
                </Typography>
                {uniqueEvents.length > 1 && (
                  <FormControl size='small' sx={{ minWidth: 200 }}>
                    <InputLabel id={`event-filter-${row._id}`}>Filtrar por evento</InputLabel>
                    <Select
                      labelId={`event-filter-${row._id}`}
                      label='Filtrar por evento'
                      value={eventFilter}
                      onChange={e => setEventFilter(e.target.value)}
                    >
                      <MenuItem value=''>Todos los eventos</MenuItem>
                      {uniqueEvents.map(ev => (
                        <MenuItem key={ev.id} value={ev.id}>{ev.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Table size='small' aria-label='purchases'>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell align='right'>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRubrics.map(rubricAssigned => (
                    <TableRow key={rubricAssigned._id}>
                      <TableCell component='th' scope='row'>
                        {rubricAssigned.name}
                      </TableCell>
                      <TableCell>{rubricAssigned.event?.name || '—'}</TableCell>
                      <TableCell align='right'>
                        {puedeGestionarRubricas && (
                          <IconButton
                            aria-label='expand row'
                            size='small'
                            onClick={() => {
                              handleShowConfirmationDialog(rubricAssigned._id, row._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleRubrics.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align='center'>
                        <Typography variant='body2' color='text.secondary'>
                          No hay rúbricas para el evento seleccionado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
